import { projectedBBoxDiagonalPx, unwrapsAntimeridian, usesShiftedFrame } from "../geometry.ts";
import type { MultiPolygon, PixelPoint, Projection, PuzzlePiece } from "../types.ts";

declare const L: any;

const SVG_NS = "http://www.w3.org/2000/svg";

export class MapView {
  readonly map: any;
  readonly projection: Projection;
  private readonly mapEl: HTMLElement;
  private readonly overlay: SVGSVGElement;
  private readonly lockedLayer: any;
  private readonly guideLayer: any;
  private readonly guides = new Map<string, any>();
  private readonly hintLayer: any;
  private ghost: SVGPathElement | null = null;
  private ghostPiece: PuzzlePiece | null = null;
  private ghostAt: PixelPoint = { x: 0, y: 0 };
  private slots: SVGPathElement | null = null;
  private slotPieces: PuzzlePiece[] = [];
  private baseZoom = 0; // 開局總覽縮放，提示不得比它更遠
  // 整組拼圖片的經度表示法：跨換日線的範圍（大洋洲、亞洲…）一律改用「負經度 +360」。
  // 視野、繪製、命中判定全部經過同一個框，任何一處漏掉就會把拼圖片丟到地圖另一端。
  private frameShift = false;
  private graticuleLayer: any = null;
  private readonly framedGeometries = new Map<string, MultiPolygon>();
  private readonly labels: Array<{ piece: PuzzlePiece; tip: any }> = [];

  constructor(mapEl: HTMLElement, overlay: SVGSVGElement) {
    this.mapEl = mapEl;
    this.overlay = overlay;
    this.map = L.map(mapEl, {
      crs: L.CRS.EPSG4326,
      attributionControl: false,
      zoomControl: true,
      worldCopyJump: false,
      touchZoom: true,
      zoomSnap: 0, // 允許小數縮放，fitBounds 才能貼合，也才能做 fitTo 的細部偏壓
      // 沒有掛 tile 圖層時 Leaflet 無從推斷縮放上限，getMaxZoom() 會是 null，
      // 導致 setZoom／zoomIn／+- 按鈕全部失效，必須明確指定
      minZoom: 0,
      maxZoom: 12,
      // zoomSnap:0 與縮放動畫衝突：沒有 tile 圖層時動畫的 transition 不會觸發，
      // 縮放永遠不會提交，+/- 與滾輪都會靜默失效。沒有圖磚本來也看不出動畫差別。
      zoomAnimation: false,
    });

    // Leaflet 用 [lat, lng]，GeoJSON 用 [lng, lat]，此處是唯一的交換點。
    // 平移也一併在這裡套用，核心的命中判定（evaluatePlacement）才會跟畫面用同一個框
    this.projection = {
      toPixel: ([lng, lat]) => {
        const p = this.map.latLngToContainerPoint([lat, this.framedLng(lng)]);
        return { x: p.x, y: p.y };
      },
    };

    this.drawGraticule();
    this.guideLayer = L.layerGroup().addTo(this.map);
    this.lockedLayer = L.layerGroup().addTo(this.map);
    this.hintLayer = L.layerGroup().addTo(this.map);

    this.map.on("zoom move zoomend moveend", () => {
      this.redrawGhost();
      this.redrawSlots();
      this.updateLabelVisibility();
    });
  }

  private framedLng(lng: number): number {
    return this.frameShift && lng < 0 ? lng + 360 : lng;
  }

  /** 單片自己跨換日線，或整組採用平移框，繪製時都要把負經度搬到 180 度以東 */
  private unwrap(piece: PuzzlePiece): boolean {
    return this.frameShift || unwrapsAntimeridian(piece.bbox);
  }

  /**
   * Leaflet 的 geoJSON 圖層自己做投影，不經過 this.projection，
   * 所以平移框下必須先把座標搬好再交給它。沒有負經度的片直接沿用原物件不複製；
   * 極難世界有 3,143 片，無條件深拷貝等於把整份資料再存一遍。
   */
  private framedGeometry(piece: PuzzlePiece): MultiPolygon {
    if (!this.unwrap(piece)) return piece.geometry;
    const cached = this.framedGeometries.get(piece.pieceId);
    if (cached) return cached;

    const hasNegative = piece.geometry.coordinates.some((polygon) =>
      polygon.some((ring) => ring.some(([lng]) => lng < 0)),
    );
    const framed = hasNegative
      ? {
        type: "MultiPolygon" as const,
        coordinates: piece.geometry.coordinates.map((polygon) =>
          polygon.map((ring) => ring.map(([lng, lat]) => [lng < 0 ? lng + 360 : lng, lat])),
        ),
      }
      : piece.geometry;
    this.framedGeometries.set(piece.pieceId, framed);
    return framed;
  }

  // 顏色一律交給 CSS class，切換主題時不必重畫任何圖層
  private drawGraticule(): void {
    this.graticuleLayer?.remove();
    const group = L.layerGroup().addTo(this.map);
    const style = { className: "graticule", weight: 1, interactive: false };
    // 平移框的可視範圍落在 0~360，畫 -180~180 的話格線會整片落在畫面外
    const [from, to] = this.frameShift ? [0, 360] : [-180, 180];
    for (let lng = from; lng <= to; lng += 30) group.addLayer(L.polyline([[-90, lng], [90, lng]], style));
    for (let lat = -60; lat <= 60; lat += 30) group.addLayer(L.polyline([[lat, from], [lat, to]], style));
    this.graticuleLayer = group;
  }

  fitTo(pieces: PuzzlePiece[]): void {
    // 必須先定框：後面的 bbox、格線、圖層全部依它換算
    this.frameShift = usesShiftedFrame(pieces.map((p) => p.bbox));
    this.framedGeometries.clear();
    this.drawGraticule();

    let minLng = Infinity, minLat = Infinity, maxLng = -Infinity, maxLat = -Infinity;
    for (const p of pieces) {
      minLng = Math.min(minLng, this.framedLng(p.bbox[0]));
      minLat = Math.min(minLat, p.bbox[1]);
      maxLng = Math.max(maxLng, this.framedLng(p.bbox[2]));
      maxLat = Math.max(maxLat, p.bbox[3]);
    }
    const bounds: [[number, number], [number, number]] = [[minLat, minLng], [maxLat, maxLng]];
    // 必須關掉動畫：aspectBias 要量的是套用後的幾何，帶動畫的話會量到套用前的舊值而算過頭
    this.map.fitBounds(bounds, { padding: [20, 20], animate: false });
    this.map.setZoom(this.map.getZoom() + this.aspectBias(bounds), { animate: false });
    this.baseZoom = this.map.getZoom();
  }

  /**
   * fitBounds 會讓整個範圍塞進畫面，但寬扁的範圍（如亞洲 1.7:1）放在直式手機（0.6:1）上，
   * 寬度先卡住，上下就空掉七成。這裡在不裁掉太多寬度的前提下把垂直空間用起來：
   * 放大到高度佔 VERTICAL_TARGET，但保證至少 MIN_VISIBLE_WIDTH 比例的寬度仍看得見。
   * 橫式與桌機算出來會 <= 1，維持原本的 fitBounds 結果不變。
   */
  private aspectBias(bounds: [[number, number], [number, number]]): number {
    const VERTICAL_TARGET = 0.82;
    const MIN_VISIBLE_WIDTH = 0.72;

    // 必須用 Leaflet 快取的 getSize()：fitBounds 用的是它，若這裡改量 DOM，
    // 視窗尺寸變動中途兩者會不一致，算出來的倍率會爆掉（實測直接衝到 maxZoom）
    const el = this.map.getSize();
    if (!el.x || !el.y) return 0;

    const a = this.map.latLngToContainerPoint([bounds[0][0], bounds[0][1]]);
    const b = this.map.latLngToContainerPoint([bounds[1][0], bounds[1][1]]);
    const width = Math.abs(b.x - a.x);
    const height = Math.abs(b.y - a.y);
    if (width < 1 || height < 1) return 0;

    const grow = Math.min(
      (el.y * VERTICAL_TARGET) / height,
      el.x / MIN_VISIBLE_WIDTH / width,
    );
    return grow > 1 ? Math.log2(grow) : 0;
  }

  showGuideOutlines(pieces: PuzzlePiece[]): void {
    this.guideLayer.clearLayers();
    this.guides.clear();
    for (const piece of pieces) {
      const layer = L.geoJSON(this.framedGeometry(piece), {
        interactive: false,
        style: { className: "guide", weight: 1, dashArray: "3 3", fill: false },
      });
      this.guides.set(piece.pieceId, layer);
      this.guideLayer.addLayer(layer);
    }
  }

  /**
   * 逐片移除而非整層重畫：所有難度都會畫輪廓後，重畫的代價跟著片數走——
   * 極難世界 3,143 片實測重畫一次 83ms，每放對一片卡一次，整局累積四分鐘以上。
   */
  removeGuideOutline(pieceId: string): void {
    const layer = this.guides.get(pieceId);
    if (!layer) return;
    this.guideLayer.removeLayer(layer);
    this.guides.delete(pieceId);
  }

  addLocked(piece: PuzzlePiece, colourClass: string, label: string): void {
    this.lockedLayer.addLayer(
      L.geoJSON(this.framedGeometry(piece), {
        interactive: false,
        style: { className: colourClass, weight: 0.8, fillOpacity: 0.55 },
      }),
    );

    const unwrap = this.unwrap(piece);
    const [lng, lat] = piece.target;
    const tip = L.tooltip({
      permanent: true,
      direction: "center",
      className: "piece-label",
      interactive: false,
      opacity: 1,
    })
      .setLatLng([lat, unwrap && lng < 0 ? lng + 360 : lng])
      .setContent(label);
    this.lockedLayer.addLayer(tip);
    this.labels.push({ piece, tip });
    this.updateLabelVisibility();
  }

  /** 語言切換後重寫所有已放置的國名 */
  relabelLocked(nameOf: (piece: PuzzlePiece) => string): void {
    for (const { piece, tip } of this.labels) tip.setContent(nameOf(piece));
  }

  /**
   * 拼圖片在畫面上太小時不顯示名稱：極難模式有 3,143 片，
   * 全部標上去會擠成一團也拖慢畫面，放大到看得清楚時才浮現。
   */
  private updateLabelVisibility(): void {
    const MIN_PX = 46;
    for (const { piece, tip } of this.labels) {
      const el = tip.getElement?.();
      if (!el) continue;
      const big = projectedBBoxDiagonalPx(piece.bbox, this.projection) >= MIN_PX;
      el.classList.toggle("label-hidden", !big);
    }
  }

  /**
   * 把地圖帶到目標附近。小國在世界視野下是次像素（梵蒂岡 0px），
   * 原本的脈動提示等於只畫一個 2.5px 的點，看不見也就沒有提示的意義。
   * 縮放到拼圖片對角線約佔畫面短邊 1/4：再小看不見，再大會失去周邊脈絡。
   */
  focusOn(piece: PuzzlePiece): void {
    const el = this.map.getSize();
    if (!el.x || !el.y) return;

    const [minLng, minLat, maxLng, maxLat] = piece.bbox;
    const spanDeg = Math.max(Math.hypot(maxLng - minLng, maxLat - minLat), 1e-6);
    const wantedPx = Math.min(el.x, el.y) * 0.25;
    // EPSG4326：每度像素 = 256 * 2^zoom / 180
    const zoom = Math.log2((wantedPx / spanDeg) * 180 / 256);
    // 下限取開局總覽：大國（俄羅斯 512px）本來就看得見，再拉遠只會丟掉周邊脈絡
    const clamped = Math.max(this.baseZoom, Math.min(this.map.getMaxZoom(), zoom));

    const unwrap = this.unwrap(piece);
    const [lng, lat] = piece.target;
    // 縮放動畫已關閉（見建構子），這裡必須用非動畫版本
    this.map.setView([lat, unwrap && lng < 0 ? lng + 360 : lng], clamped, { animate: false });
  }

  pulseTarget(piece: PuzzlePiece, ms = 2000): void {
    this.hintLayer.clearLayers();
    const layer = L.geoJSON(this.framedGeometry(piece), {
      interactive: false,
      style: { className: "hint-pulse", weight: 2.5, fillOpacity: 0.25 },
    });
    this.hintLayer.addLayer(layer);
    setTimeout(() => this.hintLayer.clearLayers(), ms);
  }

  containerPoint(ev: PointerEvent): PixelPoint {
    const r = this.mapEl.getBoundingClientRect();
    return { x: ev.clientX - r.left, y: ev.clientY - r.top };
  }

  isOverMap(ev: PointerEvent): boolean {
    const r = this.mapEl.getBoundingClientRect();
    return ev.clientX >= r.left && ev.clientX <= r.right && ev.clientY >= r.top && ev.clientY <= r.bottom;
  }

  /** 以 centroid 為原點建路徑，移動時只改 transform，不用每次 pointermove 重算幾千個點 */
  private centeredPath(geometry: MultiPolygon, origin: PixelPoint, unwrap: boolean): string {
    let d = "";
    for (const polygon of geometry.coordinates) {
      for (const ring of polygon) {
        for (let i = 0; i < ring.length; i++) {
          const [lng, lat] = ring[i];
          const p = this.projection.toPixel([unwrap && lng < 0 ? lng + 360 : lng, lat]);
          d += `${i === 0 ? "M" : "L"}${(p.x - origin.x).toFixed(1)} ${(p.y - origin.y).toFixed(1)}`;
        }
        d += "Z";
      }
    }
    return d;
  }

  /**
   * 拖曳時標出所有尚未放置的位置。只標「目前這片」的目標會直接洩漏答案——玩家只要
   * 拖著掃過地圖，圈一浮現就知道找到了。全部標出來則只透露「還有哪些空位」，
   * 手上這片屬於哪一個仍得靠地理知識判斷。
   */
  showSlots(pieces: PuzzlePiece[]): void {
    this.slotPieces = pieces;
    if (!this.slots) {
      this.slots = document.createElementNS(SVG_NS, "path");
      this.slots.setAttribute("class", "slot-dots");
      this.overlay.insertBefore(this.slots, this.overlay.firstChild);
    }
    this.redrawSlots();
  }

  hideSlots(): void {
    this.slots?.remove();
    this.slots = null;
    this.slotPieces = [];
  }

  // 幾千個點用單一 path 畫完，避免建立等量的 DOM 節點
  private redrawSlots(): void {
    if (!this.slots) return;
    const r = 4;
    let d = "";
    for (const piece of this.slotPieces) {
      const unwrap = this.unwrap(piece);
      const [lng, lat] = piece.target;
      const q = this.projection.toPixel([unwrap && lng < 0 ? lng + 360 : lng, lat]);
      d += `M${(q.x - r).toFixed(1)} ${q.y.toFixed(1)}a${r} ${r} 0 1 0 ${r * 2} 0a${r} ${r} 0 1 0 ${-r * 2} 0`;
    }
    this.slots.setAttribute("d", d);
  }

  showGhost(piece: PuzzlePiece, at: PixelPoint): void {
    this.ghostPiece = piece;
    this.ghostAt = at;
    if (!this.ghost) {
      this.ghost = document.createElementNS(SVG_NS, "path");
      this.ghost.setAttribute("class", "ghost");
      this.overlay.appendChild(this.ghost);
    }
    this.ghost.classList.remove("ghost-wrong");
    this.redrawGhost();
  }

  moveGhost(at: PixelPoint): void {
    this.ghostAt = at;
    this.ghost?.setAttribute("transform", `translate(${at.x} ${at.y})`);
  }

  /** 拼圖片的吸附基準點在畫面上的位置（跨換日線時取平移後的表示法） */
  private targetOrigin(): PixelPoint | null {
    if (!this.ghostPiece) return null;
    const unwrap = this.unwrap(this.ghostPiece);
    const [lng, lat] = this.ghostPiece.target;
    return this.projection.toPixel([unwrap && lng < 0 ? lng + 360 : lng, lat]);
  }

  private redrawGhost(): void {
    if (!this.ghost || !this.ghostPiece) return;
    const origin = this.targetOrigin();
    if (!origin) return;
    this.ghost.setAttribute(
      "d",
      this.centeredPath(this.ghostPiece.geometry, origin, this.unwrap(this.ghostPiece)),
    );
    this.ghost.setAttribute("transform", `translate(${this.ghostAt.x} ${this.ghostAt.y})`);
  }

  flashGhostWrong(): void {
    this.ghost?.classList.add("ghost-wrong");
  }

  hideGhost(): void {
    this.ghost?.remove();
    this.ghost = null;
    this.ghostPiece = null;
  }

  destroy(): void {
    this.labels.length = 0;
    this.hideGhost();
    this.hideSlots();
    this.overlay.replaceChildren();
    this.map.remove();
  }
}
